import { DateTime } from 'luxon';

export type Timespan = {
  start: DateTime<true>;
  end: DateTime<true>;
};
export type SpecialTimespan = Timespan & {
  value: SpecialRange; // This is also the i18n key for timeSpan translations
  resolution: string;
  comparisonStart?: DateTime<true>;
  comparisonEnd?: DateTime<true>;
};

export type SpecialRange =
  | `today`
  | `yesterday`
  | `last7Days`
  | `thisWeek`
  | `lastWeek`
  | `last30Days`
  | `thisMonth`
  | `lastMonth`
  | `thisQuarter`
  | `lastQuarter`
  | `thisYear`
  | `tomorrow`
  | `next7Days`
  | `nextWeek`
  | `next30Days`
  | `nextMonth`
  | `nextQuarter`;

const today = DateTime.now().startOf('day');
export const timespans: SpecialTimespan[] = [
  {
    value: 'today',
    start: today,
    end: today,
    resolution: 'day',
    comparisonStart: today.minus({ days: 7 }),
    comparisonEnd: today.minus({ days: 7 }),
  },
  {
    value: 'yesterday',
    start: today.minus({ days: 1 }),
    end: today.minus({ days: 1 }),
    comparisonStart: today.minus({ days: 8 }),
    comparisonEnd: today.minus({ days: 8 }),
    resolution: 'day',
  },
  {
    value: 'last7Days',
    start: today.minus({ days: 6 }),
    end: today,
    resolution: 'day',
    comparisonStart: today.minus({ days: 14 }),
    comparisonEnd: today.minus({ days: 7 }),
  },
  {
    value: 'thisWeek',
    start: today.startOf('week'),
    end: today.endOf('week'),
    resolution: 'day',
    comparisonStart: today.startOf('week').minus({ weeks: 1 }),
    comparisonEnd: today.minus({ weeks: 1 }),
  },
  {
    value: 'last30Days',
    start: today.minus({ days: 29 }),
    end: today,
    resolution: 'day',
    comparisonStart: today.minus({ days: 59 }),
    comparisonEnd: today.minus({ days: 30 }),
  },
  {
    value: 'thisMonth',
    start: today.startOf('month'),
    end: today.endOf('month'),
    resolution: 'day',
    comparisonStart: today.startOf('month').minus({ months: 1 }),
    comparisonEnd: today.minus({ months: 1 }).endOf('month'),
  },
  {
    value: 'lastMonth',
    start: today.minus({ months: 1 }).startOf('month'),
    end: today.minus({ months: 1 }).endOf('month'),
    resolution: 'day',
    comparisonStart: today.minus({ months: 2 }).startOf('month'),
    comparisonEnd: today.minus({ months: 2 }).endOf('month'),
  },
  {
    value: 'thisQuarter',
    start: today.startOf('quarter'),
    end: today,
    resolution: 'day',
    comparisonStart: today.startOf('quarter').minus({ quarters: 1 }),
    comparisonEnd: today.minus({ quarters: 1 }).endOf('quarter'),
  },
  {
    value: 'lastQuarter',
    start: today.minus({ quarters: 1 }).startOf('quarter'),
    end: today.minus({ quarters: 1 }).endOf('quarter'),
    resolution: 'day',
    comparisonStart: today.minus({ quarters: 2 }).startOf('quarter'),
    comparisonEnd: today.minus({ quarters: 2 }).endOf('quarter'),
  },
  {
    value: 'thisYear',
    start: today.startOf('year'),
    end: today.endOf('year'),
    resolution: 'day',
    comparisonStart: today.startOf('year').minus({ years: 1 }),
    comparisonEnd: today.minus({ years: 1 }).endOf('year'),
  },
  {
    value: 'tomorrow',
    start: today.plus({ days: 1 }),
    end: today.plus({ days: 1 }),
    resolution: 'day',
  },
  {
    value: 'next7Days',
    start: today.plus({ days: 1 }),
    end: today.plus({ days: 7 }),
    resolution: 'day',
  },
  {
    value: 'next30Days',
    start: today.plus({ days: 1 }),
    end: today.plus({ days: 30 }),
    resolution: 'day',
  },
  {
    value: 'nextQuarter',
    start: today.plus({ quarters: 1 }).startOf('quarter'),
    end: today.plus({ quarters: 1 }).endOf('quarter'),
    resolution: 'day',
  },
];

export const defaultDisplayedTimespans: SpecialRange[] = [
  'today',
  'yesterday',
  'last7Days',
  'thisWeek',
  'lastWeek',
  'last30Days',
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'lastQuarter',
  'thisYear',
];

const isFullYear = ({ start, end }: Timespan) =>
  start.hasSame(end, 'year') && start.ordinal === 1 && end.ordinal === end.daysInYear;

const isFullMonth = ({ start, end }: Timespan) =>
  start.hasSame(end, 'month') && start.day === 1 && end.day === end.daysInMonth;

const isFullWeek = ({ start, end }: Timespan) =>
  start.weekday === 1 && end.weekday === 7 && start.hasSame(end, 'week');

const numDays = (start: DateTime, end: DateTime) => end.diff(start, 'days').days + 1;

export const nextWindow = ({ start, end }: Timespan) => {
  if (isFullYear({ start, end })) {
    return {
      start: start.plus({ years: 1 }),
      end: start.plus({ years: 1 }).endOf('year'),
    };
  } else if (isFullMonth({ start, end })) {
    return {
      start: start.plus({ months: 1 }),
      end: start.plus({ months: 1 }).endOf('month'),
    };
  } else if (isFullWeek({ start, end })) {
    return {
      start: start.plus({ weeks: 1 }),
      end: end.plus({ weeks: 1 }),
    };
  } else {
    return {
      start: start.plus({ days: numDays(start, end) }),
      end: end.plus({ days: numDays(start, end) }),
    };
  }
};

export const prevWindow = ({ start, end }: Timespan) => {
  if (isFullYear({ start, end })) {
    return {
      start: start.minus({ years: 1 }),
      end: start.minus({ years: 1 }).endOf('year'),
    };
  } else if (isFullMonth({ start, end })) {
    return {
      start: start.minus({ months: 1 }),
      end: start.minus({ months: 1 }).endOf('month'),
    };
  } else if (isFullWeek({ start, end })) {
    return {
      start: start.minus({ weeks: 1 }),
      end: end.minus({ weeks: 1 }),
    };
  } else {
    return {
      start: start.minus({ days: numDays(start, end) }),
      end: end.minus({ days: numDays(start, end) }),
    };
  }
};

export const comparisonWindow = ({ start, end }: Timespan) => {
  if (isFullYear({ start, end })) {
    return {
      start: start?.minus({ years: 1 }),
      end: end?.minus({ years: 1 }),
    };
  } else if (isFullMonth({ start, end })) {
    return {
      start: start?.minus({ months: 1 }),
      end: end?.minus({ months: 1 }).endOf('month'),
    };
  } else if (start.hasSame(end, 'day')) {
    return {
      start: start?.minus({ days: 7 }),
      end: end?.minus({ days: 7 }),
    };
  } else if (isFullWeek({ start, end })) {
    return {
      start: start.minus({ weeks: 1 }).startOf('week'),
      end: end.minus({ weeks: 1 }).endOf('week'),
    };
  } else {
    return {
      start: start?.minus({ days: numDays(start, end) }),
      end: end?.minus({ days: numDays(start, end) }),
    };
  }
};

export const findSpecialRange = ({ start, end }: Timespan) => {
  return timespans.find((t) => {
    return start.hasSame(t.start, 'day') && end.hasSame(t.end, 'day');
  });
};

const separator = '~';

export const serializeTimespan = (timespan: Timespan) => {
  const specialRange = findSpecialRange(timespan);
  if (specialRange) {
    return specialRange.value;
  }
  return `${timespan.start.toISODate()}${separator}${timespan.end.toISODate()}`;
};

export const deserializeTimespan = (str: string) => {
  if (typeof str !== 'string') return timespans[0];

  const specialRange = timespans.find((t) => t.value === str);
  if (specialRange) return specialRange;

  const [start, end] = str.split(separator).map((x) => DateTime.fromISO(x));

  if (start.isValid && end.isValid) return { start, end };

  return timespans[0];
};
